/**
 * Annual target-grid preview logic (client mirror of the backend
 * `target_grid_service.py`). Used for live preview as the user edits the KPI
 * form; the server remains authoritative on save.
 *
 * Keep this in sync with strategy_management/services/target_grid_service.py.
 */
import type { Frequency, TargetPattern, TargetAggregation } from '../types';

export interface GridPeriod {
  year: number;
  period_index: number; // 1..N, stable per frequency (Q3 == 3 even if Q1/Q2 absent)
  period_label: string;
}

export interface GridCell extends GridPeriod {
  value: number;
  is_computed: boolean;
}

const PERCENT_UNITS = new Set(['%', 'percent', 'percentage', 'نسبة', 'نسبة مئوية']);

export const GRIDLESS_FREQUENCIES = new Set<Frequency>(['daily', 'weekly']);

// Verbatim Arabic period labels (from the Figma reference screens).
const MONTH_LABELS = [
  'يناير', 'فبراير', 'مارس', 'ابريل', 'مايو', 'يونيو',
  'يوليو', 'اغسطس', 'سبتمبر', 'اكتوبر', 'نوفمبر', 'ديسمبر',
];
const QUARTER_LABELS = ['الربع 1', 'الربع 2', 'الربع 3', 'الربع 4'];
const HALF_LABELS = ['النصف 1', 'النصف 2']; // OQ-1: assumed, pending designer.
const YEAR_LABEL = 'سنوي';

export function isPercentUnit(unit: string | null | undefined): boolean {
  return PERCENT_UNITS.has((unit || '').trim().toLowerCase());
}

interface PeriodDef {
  index: number;
  label: string;
}

function periodDefs(frequency: Frequency): PeriodDef[] {
  switch (frequency) {
    case 'monthly':
      return MONTH_LABELS.map((label, i) => ({ index: i + 1, label }));
    case 'quarterly':
      return QUARTER_LABELS.map((label, i) => ({ index: i + 1, label }));
    case 'half_yearly':
      return HALF_LABELS.map((label, i) => ({ index: i + 1, label }));
    case 'yearly':
      return [{ index: 1, label: YEAR_LABEL }];
    default:
      return [];
  }
}

/** Last calendar day of the given period (new Date(y, month, 0) → last day). */
function periodEndDate(frequency: Frequency, year: number, periodIndex: number): Date {
  let month: number;
  if (frequency === 'monthly') month = periodIndex;
  else if (frequency === 'quarterly') month = periodIndex * 3;
  else if (frequency === 'half_yearly') month = periodIndex * 6;
  else month = 12; // yearly
  return new Date(year, month, 0);
}

/** Public wrapper: last calendar day of a period. */
export function getPeriodEndDate(frequency: Frequency, year: number, periodIndex: number): Date {
  return periodEndDate(frequency, year, periodIndex);
}

/** Map a measurement date to its 1-based period index (mirrors backend). */
export function periodIndexForDate(frequency: Frequency, d: Date): number | null {
  switch (frequency) {
    case 'monthly':
      return d.getMonth() + 1;
    case 'quarterly':
      return Math.floor(d.getMonth() / 3) + 1;
    case 'half_yearly':
      return Math.floor(d.getMonth() / 6) + 1;
    case 'yearly':
      return 1;
    default:
      return null;
  }
}

function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parts = value.split('-').map((n) => parseInt(n, 10));
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

/**
 * Per-period target for a specific measurement date — looks up the annual-grid
 * cell whose (year, period_index) contains `dateStr`. Mirrors the backend's
 * per-period achievement target (StageCalculationService): a reading is measured
 * against ITS period's target, not the aggregated annual total. Returns the cell
 * value (string, as stored) or `fallback` when there is no grid, no
 * frequency/date, or the date's period is not in the grid (legacy KPI, gridless
 * frequency, or a date outside the target window) — callers pass the KPI's
 * overall `target_value` as the fallback.
 */
export function periodTargetForDate(
  targetValues: Array<{ year: number; period_index: number; value: string }> | null | undefined,
  frequency: Frequency | '' | string,
  dateStr: string | null | undefined,
  fallback: string | null = null,
): string | null {
  if (!targetValues || targetValues.length === 0 || !frequency || !dateStr) return fallback;
  const d = parseDate(dateStr);
  if (!d) return fallback;
  const pidx = periodIndexForDate(frequency as Frequency, d);
  if (pidx == null) return fallback;
  const year = d.getFullYear();
  const match = targetValues.find((r) => r.year === year && r.period_index === pidx);
  return match ? match.value : fallback;
}

/** Periods whose end-date falls within [start, end]; partial years allowed. */
export function generatePeriods(
  start: string | Date | null,
  end: string | Date | null,
  frequency: Frequency,
): GridPeriod[] {
  if (GRIDLESS_FREQUENCIES.has(frequency)) return [];
  const startDate = parseDate(start);
  const endDate = parseDate(end);
  if (!startDate || !endDate || endDate < startDate) return [];

  const periods: GridPeriod[] = [];
  for (let year = startDate.getFullYear(); year <= endDate.getFullYear(); year += 1) {
    for (const def of periodDefs(frequency)) {
      const pe = periodEndDate(frequency, year, def.index);
      if (pe >= startDate && pe <= endDate) {
        periods.push({ year, period_index: def.index, period_label: def.label });
      }
    }
  }
  return periods;
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function clampCell(value: number, unit: string | null | undefined): number {
  let v = Number.isFinite(value) ? value : 0;
  if (isPercentUnit(unit)) v = Math.min(100, Math.max(0, v));
  else v = Math.max(0, v);
  return round2(v);
}

/** Per-period values for a non-custom pattern, clamped per unit. */
export function generateValues(
  pattern: TargetPattern,
  firstValue: number,
  stepPercent: number,
  stepAmount: number,
  count: number,
  unit: string | null | undefined,
): number[] {
  if (count <= 0) return [];
  const first = Number.isFinite(firstValue) ? firstValue : 0;

  if (pattern === 'fixed') {
    const cell = clampCell(first, unit);
    return Array(count).fill(cell);
  }

  const values: number[] = [clampCell(first, unit)];
  const pct = Number.isFinite(stepPercent) ? stepPercent : 0;
  const amount = Number.isFinite(stepAmount) ? stepAmount : 0;

  for (let i = 1; i < count; i += 1) {
    const prev = values[i - 1];
    let next: number;
    switch (pattern) {
      case 'pct_increase':
        next = prev * (1 + pct / 100);
        break;
      case 'pct_decrease':
        next = prev * (1 - pct / 100);
        break;
      case 'num_increase':
        next = prev + amount;
        break;
      case 'num_decrease':
        next = prev - amount;
        break;
      default:
        next = prev;
    }
    values.push(clampCell(next, unit));
  }
  return values;
}

export function yearlyTotal(
  values: number[],
  unit: string | null | undefined,
  aggregation: TargetAggregation,
): number {
  if (values.length === 0) return 0;
  if (isPercentUnit(unit)) return values[values.length - 1];
  if (aggregation === 'sum') return round2(values.reduce((a, b) => a + b, 0));
  return values[values.length - 1];
}

export function overallTotal(
  values: number[],
  unit: string | null | undefined,
  aggregation: TargetAggregation,
): number {
  return yearlyTotal(values, unit, aggregation);
}

export function groupByYear(cells: GridCell[]): Map<number, GridCell[]> {
  const out = new Map<number, GridCell[]>();
  for (const cell of cells) {
    const arr = out.get(cell.year) || [];
    arr.push(cell);
    out.set(cell.year, arr);
  }
  return out;
}

export interface GridComputeInput {
  frequency: Frequency;
  pattern: TargetPattern;
  unit: string;
  start: string | null;
  end: string | null;
  stepPercent: number;
  stepAmount: number;
  /**
   * User-entered values keyed "year:period_index".
   * - custom: one value per period per year (all editable).
   * - fixed / increase / decrease: the per-year base lives at "year:1"; the
   *   remaining periods of that year are computed from it WITHIN the year
   *   (compounding/stepping resets every year).
   */
  inputs: Record<string, number>;
}

/** Build the full cell list for the current config, grouped & generated per year. */
export function computeGrid(input: GridComputeInput): GridCell[] {
  const periods = generatePeriods(input.start, input.end, input.frequency);
  if (periods.length === 0) return [];

  const byYear = groupPeriodsByYear(periods);
  const cells: GridCell[] = [];

  for (const [year, yearPeriods] of byYear) {
    if (input.pattern === 'custom') {
      for (const p of yearPeriods) {
        cells.push({
          ...p,
          value: clampCell(input.inputs[`${year}:${p.period_index}`] ?? 0, input.unit),
          is_computed: false,
        });
      }
    } else {
      const base = input.inputs[`${year}:1`] ?? 0;
      const values = generateValues(
        input.pattern,
        base,
        input.stepPercent,
        input.stepAmount,
        yearPeriods.length,
        input.unit,
      );
      yearPeriods.forEach((p, i) => {
        cells.push({ ...p, value: values[i], is_computed: true });
      });
    }
  }
  return cells;
}

function groupPeriodsByYear(periods: GridPeriod[]): Map<number, GridPeriod[]> {
  const out = new Map<number, GridPeriod[]>();
  for (const p of periods) {
    const arr = out.get(p.year) || [];
    arr.push(p);
    out.set(p.year, arr);
  }
  return out;
}
